import React, { Fragment, useRef } from 'react';
import CompareQuotesStyle from './style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleLeft,
    faCircleRight,
    faMap,
    faTimes,
    faMoon,
    faUsers,
    faGolfBall,
    faExchange,
    faPlane,
    faFlag,
    faStar,
    faCar,
    faTaxi,
    faTrophy,
} from '@fortawesome/free-solid-svg-icons';
import { Flex, Spin, Carousel, Skeleton } from 'antd';
import { useSelector } from 'react-redux';
import { multipleQuotesSelector } from 'src/redux/selectors/multipleQuotesSelector';
import useIsPreview from 'src/hooks/useIsPreview';
import useQueryApi from 'src/hooks/useApiQuery';
import useIsMobile from 'src/hooks/useIsMobile';
import { getCompareQuotes } from 'src/api/apiGetCompareQuotes';
import { formatCurrency, modifyURL } from 'src/const/contain';
import { contentfulSelector } from 'src/redux/selectors/contentfulSelector';
import { getImageApi } from 'src/api/apiGetImage';
import moment from 'moment';
import { DATE_FORMAT } from 'src/const/common';

interface CompareQuotesProps {
    onClose: () => void;
}

const CompareQuotesComponent: React.FC<CompareQuotesProps> = ({ onClose }) => {
    const isPreview = useIsPreview();
    const isMobile = useIsMobile();
    const carouselRef = useRef<any>(null);

    const { ids: quotesArray = [], compareQuotes = [] } = useSelector(
        multipleQuotesSelector,
    );

    const { data = compareQuotes || [], isLoading }: any = useQueryApi(
        ['compareQuotes', quotesArray.join(',')],
        () => {
            return getCompareQuotes(`?quoteRefs=${quotesArray.join(',')}`);
        },

        {
            enabled: quotesArray.length > 1 && isPreview,
        },
    );

    const renderContentDesktop = () => (
        <Flex
            justify={data.length > 2 ? 'space-between' : 'space-around'}
            gap={16}
        >
            {data.map((item: any, index: number) => (
                <CompareQuoteItem
                    key={index}
                    compareQuoteItem={item}
                    index={index}
                    compareQuotesLength={quotesArray.length}
                />
            ))}
        </Flex>
    );
    const renderContentMobile = () => (
        <div className="box-carousel">
            <FontAwesomeIcon
                icon={faCircleLeft}
                className="icon-circle btn-prev"
                onClick={() => carouselRef.current?.prev()}
            />
            <FontAwesomeIcon
                icon={faCircleRight}
                className="icon-circle btn-next"
                onClick={() => carouselRef.current?.next()}
            />
            <Carousel ref={carouselRef} dots={false}>
                {data.map((item: any, index: number) => (
                    <CompareQuoteItem
                        key={index}
                        compareQuoteItem={item}
                        index={index}
                        compareQuotesLength={quotesArray.length}
                    />
                ))}
            </Carousel>
        </div>
    );

    return (
        <CompareQuotesStyle>
            <div className="box-header">
                <div className="container-ydp">
                    <Flex align="center" justify="space-between">
                        <span>Your Quotes at a Glance</span>
                        <FontAwesomeIcon
                            icon={faTimes}
                            className="icon-close"
                            onClick={onClose}
                        />
                    </Flex>
                </div>
            </div>
            <div className="content-compare">
                <div className="container-ydp">
                    {isLoading ? (
                        <div style={{ textAlign: 'center', margin: '30px 0' }}>
                            <Spin size="large" />
                        </div>
                    ) : isMobile ? (
                        renderContentMobile()
                    ) : (
                        renderContentDesktop()
                    )}
                </div>
            </div>
        </CompareQuotesStyle>
    );
};

export default CompareQuotesComponent;

interface CompareQuoteItemProps {
    compareQuoteItem: any;
    index: number;
    compareQuotesLength: number;
}
const CompareQuoteItem: React.FC<CompareQuoteItemProps> = ({
    index,
    compareQuoteItem,
    compareQuotesLength,
}) => {
    const {
        id,
        title,
        mainImage,
        isEvent,
        tourName = '',
        price = {},
        groups,
        dbPriceGroups,
        unallocatedAccommodation,
    } = compareQuoteItem;
    const { totalPrice } = price;
    const {
        defaultImage,
        theMastersImage,
        theOpenImage,
        thePlayersChampsImage,
        theRyderCupImage,
        theUsOpenImage,
        solheimCupImage,
        raceToDubaiImage,
        pgaTourImage,
        dpWorldTourImage,
    } = useSelector(contentfulSelector);

    const getDefaultImageId = () => {
        let imageField: any = defaultImage;
        if (isEvent) {
            switch (true) {
                case tourName.includes('Masters'):
                    imageField = theMastersImage;
                    break;
                case tourName.includes('Ryder'):
                    imageField = theRyderCupImage;
                    break;
                case tourName.includes('The Open'):
                    imageField = theOpenImage;
                    break;
                case tourName.includes('US Open'):
                    imageField = theUsOpenImage;
                    break;
                case tourName.includes('Solheim'):
                    imageField = solheimCupImage;
                    break;
                case tourName.includes('Players'):
                    imageField = thePlayersChampsImage;
                    break;
                case tourName.includes('Dubai'):
                    imageField = raceToDubaiImage;
                    break;
                case tourName.includes('PGA'):
                    imageField = pgaTourImage;
                    break;
                case tourName.includes('DP'):
                    imageField = dpWorldTourImage;
                    break;
                default:
                    imageField = defaultImage;
                    break;
            }
        }

        return imageField?.sys?.id || '';
    };

    const { data: defaultImageField } = useQueryApi(
        ['image', getDefaultImageId()],
        () => getImageApi(getDefaultImageId()),
        {
            enabled: !mainImage && !!getDefaultImageId(),
        },
    );

    const addHttpsImage = (url: string) => {
        if (url) {
            return `http:${url}`;
        }
        return '';
    };

    const urlImage =
        mainImage ??
        addHttpsImage((defaultImageField as any)?.fields?.file?.url);

    const resultGroups =
        groups && typeof groups === 'object'
            ? Object?.entries(groups)
                  ?.map(([group, items]) => ({
                      group,
                      items,
                  }))
                  .sort((a, b) => a.group.localeCompare(b.group))
            : [];

    const renderGroupItem = (groupItem: any) => {
        const { group, items } = groupItem;
        const {
            price,
            carHire = [],
            flight = [],
            transfer = [],
            accommodations = [],
            totalPerson,
            returnAirportTransfer = false,
            golf = [],
            hasGolfExtra = false,
            hasFlightExtra = false,
            hasTourEvent = false,
        } = items;

        const flightDepartureName = flight[0]?.departure?.name;
        const flightArrivalName = flight[0]?.arrival?.name;

        const carHirePickUp = carHire[0]?.pickUp?.location || '';
        const carHireNights = carHire[0]?.nights || 0;

        const pricePerPerson = dbPriceGroups[group] || price;

        const results = transfer.map((tr: any) => {
            if (tr.carType.includes('Airport')) {
                return 'Airport';
            } else if (tr.carType.includes('Golf')) {
                return 'Golf';
            } else if (tr.carType.includes('Hotel')) {
                return 'Hotel';
            } else {
                return '';
            }
        });

        const renderAccommodation = () => (
            <>
                {accommodations?.map((ac: any, index: any) => {
                    const extra = ac?.extra;
                    const duration = ac?.duration;
                    return (
                        <Fragment key={index}>
                            <div className="item">
                                <FontAwesomeIcon
                                    className="icon-detail"
                                    icon={faUsers}
                                />
                                <div className="titled-detail">
                                    {totalPerson}{' '}
                                    {Number(totalPerson) === 1 && 'person'}{' '}
                                    {Number(totalPerson) > 1 && 'people'}{' '}
                                    arriving on{' '}
                                    {moment(ac?.startDate || 0)
                                        .utc()
                                        .format(DATE_FORMAT)}
                                </div>
                            </div>
                            <div className="item">
                                <FontAwesomeIcon
                                    className="icon-detail"
                                    icon={faMap}
                                />
                                <div className="title-detail">
                                    Staying at {ac?.name}, {ac?.roomType}
                                    {`${
                                        ac?.singleOccupancy
                                            ? ' (Single Occupancy)'
                                            : ''
                                    }`}
                                </div>
                            </div>
                            <div className="item">
                                <FontAwesomeIcon
                                    className="icon-detail"
                                    icon={faMoon}
                                />
                                <div className="title-detail">
                                    {duration}{' '}
                                    {Number(duration) > 1 ? 'Nights' : 'Night'}
                                    {ac?.boardBasis
                                        ? `, ${ac?.boardBasis}`
                                        : ''}
                                    {extra?.map(
                                        (itemExtras: any, indexEx: any) => {
                                            return (
                                                <span key={indexEx}>
                                                    {itemExtras?.nbrCode !==
                                                        ac?.roomType &&
                                                    itemExtras?.nbrCode !==
                                                        ac?.boardBasis &&
                                                    itemExtras?.nbrCode !==
                                                        'Spa Package'
                                                        ? `, ${itemExtras?.nbrCode}`
                                                        : ''}
                                                </span>
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                        </Fragment>
                    );
                })}
            </>
        );

        const renderGolfs = () => (
            <>
                {golf?.map((item: any, index: any) => {
                    return (
                        <div key={index}>
                            <div className="item">
                                <FontAwesomeIcon
                                    className="icon-detail"
                                    icon={tourName ? faGolfBall : faFlag}
                                />
                                <div className="title-detail">
                                    {item?.rounds}{' '}
                                    {item?.rounds > 1 ? 'Rounds' : 'Round'} of
                                    Golf at {item?.serviceDescription}
                                    {item?.documentDescription
                                        ? `, ${item?.documentDescription}`
                                        : ''}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </>
        );

        const renderFlights = () => (
            <>
                {flight?.length > 0 && (
                    <div className="item">
                        <FontAwesomeIcon
                            className="icon-detail"
                            icon={faPlane}
                        />
                        <div className="title-detail">
                            {' '}
                            {flight?.length > 1
                                ? 'Return Flights'
                                : 'Flight'}{' '}
                            from {flightDepartureName} to {flightArrivalName}
                        </div>
                    </div>
                )}
            </>
        );

        const renderUnallocatedAccommodation = () => (
            <>
                {unallocatedAccommodation && (
                    <div className="item">
                        <FontAwesomeIcon
                            className="icon-detail"
                            icon={faMoon}
                        />
                        <div className="title-detail">
                            Accommodation as per your itinerary
                        </div>
                    </div>
                )}
            </>
        );
        return (
            <>
                <Flex justify="space-between" gap={10} className="price">
                    <span>{`${
                        resultGroups.length > 1 ? `${group} p` : 'P'
                    }rice per person`}</span>
                    <span>£{formatCurrency(pricePerPerson)}</span>
                </Flex>
                {hasTourEvent && (
                    <div className="item">
                        <FontAwesomeIcon
                            className="icon-detail"
                            icon={faTrophy}
                        />
                        <div className="title-detail">{tourName}</div>
                    </div>
                )}
                {renderAccommodation()}
                {renderGolfs()}
                {hasGolfExtra && (
                    <div className="item">
                        <div className="iconDetail">
                            <FontAwesomeIcon
                                icon={tourName ? faGolfBall : faFlag}
                                className="icon-detail"
                            />
                        </div>
                        <div className="title-detail">Golf extras</div>
                    </div>
                )}
                {renderFlights()}
                {hasFlightExtra && (
                    <div className="item">
                        <FontAwesomeIcon
                            className="icon-detail"
                            icon={faStar}
                        />
                        <div className="title-detail">Flight extras</div>
                    </div>
                )}
                {returnAirportTransfer && (
                    <div className="item">
                        <FontAwesomeIcon
                            className="icon-detail"
                            icon={faExchange}
                        />
                        <div className="title-detail">Airport transfers</div>
                    </div>
                )}
                {transfer?.length > 0 && (
                    <div className="item">
                        <FontAwesomeIcon
                            className="icon-detail"
                            icon={faTaxi}
                        />
                        <div className="titleDetail">
                            {results[1]}{' '}
                            {results[1] ? 'transfers' : 'Transfers'}
                        </div>
                    </div>
                )}
                {carHire?.length > 0 && (
                    <div className="item">
                        <FontAwesomeIcon className="icon-detail" icon={faCar} />
                        <div className="title-detail">
                            Car hire pick-up from {carHirePickUp} for{' '}
                            {carHireNights}{' '}
                            {Number(carHireNights) > 1 ? 'days' : 'day'}
                        </div>
                    </div>
                )}
                {renderUnallocatedAccommodation()}
            </>
        );
    };

    const renderGroups = () => (
        <>
            {resultGroups.map((item, index) => (
                <Fragment key={index}>{renderGroupItem(item)}</Fragment>
            ))}
        </>
    );
    return (
        <div className="item-compare">
            <div className="num">{`QUOTE ${
                index + 1
            }/${compareQuotesLength}`}</div>

            <div className="item-content">
                {urlImage ? (
                    <div className="main-image">
                        <img alt="" src={modifyURL(urlImage, 500)} />
                    </div>
                ) : (
                    <Skeleton.Image
                        active={true}
                        style={{ width: '100%', height: 190 }}
                    />
                )}
                <div className="title">
                    <span>{title}</span>
                </div>
                <div className="reference">Quote reference: {id}</div>
                <div className="box-detail">
                    <Flex
                        className="total-price"
                        justify="space-between"
                        gap={10}
                    >
                        <span>Total Price</span>
                        <span>£{formatCurrency(totalPrice)}</span>
                    </Flex>
                    {renderGroups()}
                </div>
            </div>
        </div>
    );
};
